import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useLocation } from 'wouter';
import { Signals, useSignaling } from '@/contexts/SignalingContext';
import { Crown, Eye, EyeOff, Globe, Search, X } from 'lucide-react';
import { COUNTRIES, Country } from '@/contexts/GeoFilterContext';
import { useAuth } from '@/contexts/AuthContext';

interface GeoFilterState {
  mode: 'whitelist' | 'blacklist';
  countries: Country[];
  isEnabled: boolean;
}

const initialState: GeoFilterState = {
  mode: 'whitelist',
  countries: [],
  isEnabled: false
};

export function GeoFilterDropdown() {
  const [ geoFilter, setGeoFilter ] = useState<GeoFilterState>(initialState);
  const [ searchTerm, setSearchTerm ] = useState('');
  const [ showCountryList, setShowCountryList ] = useState(false);
  const { user } = useAuth();

  const { sendSignal, isConnected } = useSignaling();
  const [ , navigate ] = useLocation();

  const userPlan = user?.maybeSubscriptionInfo?.plan?.toLowerCase() || 'free';

  const filteredCountries = COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 10);

  // Helper to send filter updates to backend
  const sendFilterUpdate = (newState: GeoFilterState) => {
    if (isConnected) {
      sendSignal({
        signal: Signals.UpdateGeoFilter,
        geoFilter: {
          enabled: newState.isEnabled,
          mode: newState.mode,
          countries: newState.countries.map(c => c.code)
        }
      });
    }
  };

  const toggleGeoFilter = () => {
    const newState = { ...geoFilter, isEnabled: !geoFilter.isEnabled };
    setGeoFilter(newState);
    sendFilterUpdate(newState);
  };

  const setFilterMode = (mode: 'whitelist' | 'blacklist') => {
    const newState = {
      ...geoFilter,
      mode,
      countries: []
    };
    setGeoFilter(newState);
    sendFilterUpdate(newState);
  };

  const addCountry = (country: Country) => {
    // Check if country already exists or limit reached
    if (geoFilter.countries.some(c => c.code === country.code) || geoFilter.countries.length >= 3) {
      return;
    }

    const newState = {
      ...geoFilter,
      countries: [ ...geoFilter.countries, country ]
    };
    setGeoFilter(newState);
    sendFilterUpdate(newState);
    setSearchTerm('');
    setShowCountryList(false);
  };

  const removeCountry = (countryCode: string) => {
    const newState = {
      ...geoFilter,
      countries: geoFilter.countries.filter(c => c.code !== countryCode)
    };
    setGeoFilter(newState);
    sendFilterUpdate(newState);
  };

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="bg-transparent hover:bg-[#273449] text-white border border-slate-700 p-2"
            >
              {!geoFilter.isEnabled ? (
                <span className="text-sm">🌍</span>
              ) : (
                geoFilter.countries.length > 1 ? (
                  <span className="text-xs font-bold bg-green-600 text-white px-1 rounded">
                    {geoFilter.countries.length}
                  </span>
                ) : geoFilter.countries?.length === 1 ? (
                  <span className={`text-sm ${geoFilter.mode === 'blacklist' ? 'opacity-50' : ''}`}>
                    {geoFilter.countries[0]?.flag || '🌍'}
                  </span>
                ) : (
                  <span className="text-sm">🌍</span>
                )
              )}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {!geoFilter.isEnabled
            ? 'Geo Filter: Disabled'
            : `Geo Filter: ${geoFilter.countries?.length || 0} countries selected`
          }
        </TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="end" className="bg-[#1C2431] border-slate-700 w-80 p-4 overflow-visible">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="w-4 h-4 text-blue-400"/>
          <span className="text-white font-semibold text-sm">Geo Filtering</span>
        </div>
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between mb-3 p-2 bg-[#0E1525] rounded">
          <span className="text-white text-sm">Enable Geo Filtering</span>
          <Switch
            checked={geoFilter.isEnabled}
            onCheckedChange={toggleGeoFilter}
            disabled={userPlan === 'free'}
          />
        </div>
        {userPlan === 'free' && (
          <div className="text-center p-3 bg-purple-900/20 border border-purple-500/30 rounded mb-3">
            <Crown className="w-4 h-4 text-yellow-400 mx-auto mb-1"/>
            <p className="text-slate-300 text-xs mb-2">Premium Feature</p>
            <Button
              onClick={() => navigate('/plans')}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Upgrade
            </Button>
          </div>
        )}
        {geoFilter.isEnabled && userPlan !== 'free' && (
          <>
            {/* Mode Selection */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setFilterMode('whitelist')}
                className={`flex-1 flex items-center justify-center gap-1 py-2 px-2 rounded text-xs font-medium transition-colors ${
                  geoFilter.mode === 'whitelist'
                    ? 'bg-green-600 text-white'
                    : 'bg-[#0E1525] text-slate-400 hover:text-white'
                }`}
              >
                <Eye className="w-3 h-3"/>
                Whitelist
              </button>
              <button
                onClick={() => setFilterMode('blacklist')}
                className={`flex-1 flex items-center justify-center gap-1 py-2 px-2 rounded text-xs font-medium transition-colors ${
                  geoFilter.mode === 'blacklist'
                    ? 'bg-red-600 text-white'
                    : 'bg-[#0E1525] text-slate-400 hover:text-white'
                }`}
              >
                <EyeOff className="w-3 h-3"/>
                Blacklist
              </button>
            </div>
            {/* Selected Countries */}
            {geoFilter.countries?.length > 0 && (
              <div className="mb-3">
                <div className="flex flex-wrap gap-1">
                  {geoFilter.countries.map((country: Country) => (
                    <div
                      key={country.code}
                      className={`flex items-center gap-1 ${
                        geoFilter.mode === 'whitelist'
                          ? 'bg-green-600/20 border border-green-600/30'
                          : 'bg-red-600/20 border border-red-600/30'
                      } px-2 py-1 rounded text-xs`}
                    >
                      <span>{country.flag}</span>
                      <span className="text-white">{country.name}</span>
                      <button
                        onClick={() => removeCountry(country.code)}
                        className={
                          geoFilter.mode === 'whitelist'
                            ? 'text-green-400 hover:text-green-300'
                            : 'text-red-400 hover:text-red-300'
                        }
                      >
                        <X className="w-3 h-3"/>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Country Search */}
            {((geoFilter.countries?.length || 0) < 3) && (
              <div className="relative" style={{ zIndex: 10000 }}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 text-slate-400"/>
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowCountryList(true);
                    }}
                    onFocus={() => setShowCountryList(true)}
                    className="w-full pl-8 pr-3 py-2 bg-[#0E1525] border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:border-blue-500 focus:outline-none"
                  />
                </div>
                {showCountryList && searchTerm && (
                  <div
                    className="absolute top-full left-0 right-0 mt-1 bg-[#0E1525] border border-slate-600 rounded shadow-xl max-h-40 overflow-y-auto z-[9999]">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country: Country) => (
                        <button
                          key={country.code}
                          onClick={() => addCountry(country)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#1C2431] transition-colors"
                          disabled={geoFilter.countries.some(c => c.code === country.code)}
                        >
                          <span>{country.flag}</span>
                          <span className="text-white text-sm">{country.name}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-slate-400 text-sm">
                        No countries found
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
